import { ROUTES } from '@af/shared';
import {
  QueryCache,
  QueryClient,
  type QueryKey,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { activeOrg } from './active-org';
import { ApiClientError, type BodyMap, call } from './api';
import { auth } from './auth';
import { OrgEvent } from './contracts';
import { orgEvents } from './org-events';
import { fallbackPartsFromEvents } from './run-reducer';
import { connectSSE } from './sse';

export const qk = {
  channels: ['channels'] as const,
  channelPosts: (id: string) => ['channels', id, 'posts'] as const,
  channelThread: (id: string, thread: string) => ['channels', id, 'thread', thread] as const,
  channelSearch: (q: string) => ['channels', 'search', q] as const,
  session: ['session'] as const,
  setup: ['setup'] as const,
  me: ['me'] as const,
  companyFallback: ['org', 'subscription-fallback'] as const,
  orgStatus: ['org', 'status'] as const,
  onboarding: ['onboarding'] as const,
  orgs: ['orgs'] as const,
  members: ['members'] as const,
  agents: ['agents'] as const,
  agent: (id: string) => ['agents', id] as const,
  agentVersions: (id: string) => ['agents', id, 'versions'] as const,
  agentWorkspaces: (id: string) => ['agents', id, 'workspaces'] as const,
  agentDelegationStats: (id: string) => ['agents', id, 'delegation-stats'] as const,
  providers: ['providers'] as const,
  models: ['models'] as const,
  conversations: (agentId?: string) => ['conversations', agentId ?? 'all'] as const,
  messages: (id: string) => ['messages', id] as const,
  runs: (q?: Record<string, unknown>) => ['runs', q ?? {}] as const,
  run: (id: string) => ['run', id] as const,
  tasks: ['tasks'] as const,
  approvals: (status?: string) => ['approvals', status ?? 'all'] as const,
  questions: ['questions'] as const,
  egressRequests: ['egressRequests'] as const,
  usage: (q: Record<string, unknown>) => ['usage', q] as const,
  budgets: ['budgets'] as const,
  notifications: ['notifications'] as const,
  audit: ['audit'] as const,
  passkeys: ['passkeys'] as const,
  triggers: (agentId?: string) => ['triggers', agentId ?? 'all'] as const,
  triggerFires: (id: string) => ['triggers', 'fires', id] as const,
  mcpServers: ['mcp-servers'] as const,
  toolPolicies: ['tool-policies'] as const,
  sessions: ['sessions'] as const,
  memberList: ['members', 'list'] as const,
  auditFacets: ['audit', 'facets'] as const,
  notifyChannels: ['notify', 'channels'] as const,
  apiTokens: ['api-tokens'] as const,
  notifyRules: ['notify', 'rules'] as const,
  deliveries: ['notify', 'deliveries'] as const,
  telegram: ['notify', 'telegram'] as const,
  flows: ['flows'] as const,
  flow: (id: string) => ['flows', id] as const,
  flowVersions: (id: string) => ['flows', id, 'versions'] as const,
  flowSuggestions: (id: string) => ['flows', id, 'suggestions'] as const,
  skills: ['skills'] as const,
  skill: (id: string) => ['skills', id] as const,
  skillVersions: (id: string) => ['skills', id, 'versions'] as const,
  skillSuggestions: ['skills', 'suggestions'] as const,
  skillUsage: ['skills', 'usage'] as const,
  recordings: ['recordings'] as const,
  recording: (id: string) => ['recordings', id] as const,
  recordingEvents: (id: string) => ['recordings', id, 'events'] as const,
};

/** Toast an API error; `2fa_required` gets a direct link to the security settings. */
export function notifyError(err: unknown) {
  if (err instanceof ApiClientError && err.code === '2fa_required') {
    toast.error('Two-factor authentication required for this action', {
      id: '2fa_required',
      description: 'Owners and admins must enroll TOTP or a passkey first.',
      action: { label: 'Set up 2FA', onClick: () => window.location.assign('/settings/security') },
    });
    return;
  }
  toast.error((err as Error)?.message || 'Action failed');
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (err, query) => {
        if (query.meta?.silent) return;
        if (err instanceof ApiClientError && (err.status === 401 || err.status === 404)) return;
        toast.error(err.message || 'Request failed', { id: String(query.queryHash) });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        retry: (count, err) => !(err instanceof ApiClientError && err.status < 500) && count < 2,
        refetchOnWindowFocus: true,
      },
      mutations: {
        onError: (err) => notifyError(err),
      },
    },
  });
}

// ---------- session / setup ----------
export const sessionQuery = queryOptions({
  queryKey: qk.session,
  queryFn: () => auth.getSession(),
  staleTime: 60_000,
  refetchInterval: 30_000,
  meta: { silent: true },
});

export const setupQuery = queryOptions({
  queryKey: qk.setup,
  queryFn: async () => {
    try {
      return await call('setupStatus');
    } catch {
      return { needsSetup: false };
    }
  },
  staleTime: 60_000,
  refetchInterval: 30_000,
  meta: { silent: true },
});

export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: async () => {
    const me = await call('me');
    // Pin this tab to the company it opened in (see active-org.ts).
    if (me.organization && activeOrg.get() !== me.organization.id) activeOrg.set(me.organization.id);
    return me;
  },
  staleTime: 60_000,
  refetchInterval: 30_000,
  meta: { silent: true },
});

export const useMe = () => useQuery(meQuery);
export const useCompanyFallbackPolicy = () =>
  useQuery({ queryKey: qk.companyFallback, queryFn: () => call('companyFallbackPolicy') });
export const useOrgStatus = () =>
  useQuery({ queryKey: qk.orgStatus, queryFn: () => call('orgStatus'), refetchInterval: 30_000 });
/** First-run checklist counts; cheap COUNT queries, always refetched on mount. */
export const useOnboardingStatus = (enabled = true) =>
  useQuery({
    queryKey: qk.onboarding,
    queryFn: () => call('onboardingStatus'),
    staleTime: 0,
    enabled,
    meta: { silent: true },
  });
/** Companies the user belongs to, with live numbers (switcher, overview, palette). */
export const useCompanies = () =>
  useQuery({ queryKey: qk.orgs, queryFn: () => call('listCompanies'), staleTime: 30_000 });

// ---------- resources ----------
export const useAgents = () => useQuery({ queryKey: qk.agents, queryFn: () => call('listAgents') });
export const useAgent = (id: string) =>
  useQuery({ queryKey: qk.agent(id), queryFn: () => call('getAgent', { params: { id } }) });
export const useAgentVersions = (id: string) =>
  useQuery({ queryKey: qk.agentVersions(id), queryFn: () => call('listAgentVersions', { params: { id } }) });
export const useAgentWorkspaces = (id: string) =>
  useQuery({
    queryKey: qk.agentWorkspaces(id),
    queryFn: () => call('listAgentWorkspaces', { params: { id } }),
    // Actions run in the worker; poll briefly so their result shows up.
    refetchInterval: 15_000,
  });
export const useAgentDelegationStats = (id: string) =>
  useQuery({
    queryKey: qk.agentDelegationStats(id),
    queryFn: () => call('agentDelegationStats', { params: { id } }),
  });
export const useProviders = () => useQuery({ queryKey: qk.providers, queryFn: () => call('listProviders') });
export const useModels = (enabled = true) =>
  useQuery({ queryKey: qk.models, queryFn: () => call('listModels'), enabled });
/** True when an enabled model has no price (costs are underestimated). */
export const usePriceMissing = (enabled: boolean) => {
  const models = useModels(enabled);
  return !!models.data?.some((m) => m.enabled && !m.price);
};
export const useConversations = (agentId?: string) =>
  useQuery({
    queryKey: qk.conversations(agentId),
    queryFn: () => call('listConversations', { query: { agentId } }),
  });
/** Messages loaded per page: the newest page first, older ones when the user scrolls up. */
export const MESSAGE_PAGE = 30;

/**
 * Conversation messages, oldest first. Only the newest page is fetched up front; `loadOlder` fetches the
 * page before the oldest loaded message (keyset by message id). Returns the same `data` array as before.
 */
export function useMessages(id: string) {
  const q = useInfiniteQuery({
    queryKey: qk.messages(id),
    enabled: !!id,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call('getMessages', { params: { id }, query: { limit: MESSAGE_PAGE, before: pageParam } }),
    // pages[0] is the newest page; its first item is the oldest message of that page.
    getNextPageParam: (last) => (last.length >= MESSAGE_PAGE ? last[0]?.id : undefined),
  });
  const data = useMemo(() => {
    if (!q.data) return undefined;
    const seen = new Set<string>();
    return [...q.data.pages]
      .reverse()
      .flat()
      .filter((m) => !seen.has(m.id) && !!seen.add(m.id));
  }, [q.data]);
  const { fetchNextPage, isFetchingNextPage } = q;
  const loadOlder = useCallback(() => {
    if (!isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, isFetchingNextPage]);
  return {
    data,
    isPending: q.isPending,
    error: q.error,
    hasOlder: !!q.hasNextPage,
    loadingOlder: isFetchingNextPage,
    loadOlder,
  };
}
export const useRuns = (q?: { agentId?: string; status?: string; taskId?: string; rootRunId?: string }) =>
  useQuery({ queryKey: qk.runs(q), queryFn: () => call('listRuns', { query: q }), refetchInterval: 15_000 });
export const useRunContext = (id: string) =>
  useQuery({ queryKey: ['run', id, 'context'], queryFn: () => call('getRunContext', { params: { id } }) });
export const useRun = (id: string) =>
  useQuery({
    queryKey: qk.run(id),
    queryFn: () => call('getRun', { params: { id } }),
    refetchInterval: 5_000,
  });
/** Saved chat messages omit logs; recover the actual limit and usage from the run's events. */
export const useRunLimitDetail = (runId: string | null | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['run', runId, 'limit-detail'],
    enabled: !!runId && enabled,
    queryFn: async () => {
      let after = 0;
      let detail: string | null = null;
      for (;;) {
        const rows = await call('runEvents', {
          params: { id: runId! },
          query: { after, limit: 500, type: 'log' },
        });
        for (const row of rows) {
          const p = row.payload;
          if (
            p.level === 'warn' &&
            typeof p.message === 'string' &&
            /cap reached|budget used up|Budget reached/.test(p.message)
          )
            detail = p.message;
        }
        if (rows.length < 500) return detail;
        after = rows[rows.length - 1]!.seq;
      }
    },
  });
/**
 * Model switches (Claude ⇄ Codex) of a run, from its stored events. A finished run is shown from its saved
 * message, which carries no events, so the chat asks for just these rows once the run is over.
 */
export const useRunFallbacks = (runId: string | null | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['run', runId, 'fallbacks'],
    enabled: !!runId && enabled,
    queryFn: async () =>
      fallbackPartsFromEvents(
        await call('runEvents', {
          params: { id: runId! },
          query: { after: 0, limit: 50, type: 'model-fallback' },
        }),
      ),
  });
export const useTasks = () =>
  useQuery({ queryKey: qk.tasks, queryFn: () => call('listTasks', { query: { limit: 200 } }) });
export const useApprovals = (status?: string) =>
  useQuery({ queryKey: qk.approvals(status), queryFn: () => call('listApprovals', { query: { status } }) });
/** Open and recently answered `ask_human` questions (the server is the source, so they survive a refresh). */
export const useEgressRequests = () =>
  useQuery({ queryKey: qk.egressRequests, queryFn: () => call('listEgressRequests', { query: {} }) });
export const useQuestions = () =>
  useQuery({ queryKey: qk.questions, queryFn: () => call('listQuestions', { query: {} }) });
export const useUsage = (
  q: {
    from: string;
    to: string;
    groupBy?: string;
    agentId?: string;
    work?: 'runs' | 'background';
    billing?: 'api' | 'subscription';
  },
  enabled = true,
) => useQuery({ queryKey: qk.usage(q), queryFn: () => call('usage', { query: q }), enabled });
export const useBudgets = () => useQuery({ queryKey: qk.budgets, queryFn: () => call('listBudgets') });
export const useNotifications = () =>
  useQuery({
    queryKey: qk.notifications,
    queryFn: () => call('listNotifications', { query: { limit: 200 } }),
  });
export const useUnreadCount = () =>
  useQuery({
    queryKey: [...qk.notifications, 'unread'],
    queryFn: async () => (await call('listNotifications', { query: { unread: 'true', limit: 100 } })).length,
    refetchInterval: 60_000,
    meta: { silent: true },
  });
export const useTriggers = (agentId?: string) =>
  useQuery({
    queryKey: qk.triggers(agentId),
    queryFn: () => call('listTriggers', { query: { agentId, limit: 200 } }),
  });
export const useTriggerFires = (id: string, enabled = true) =>
  useQuery({
    queryKey: qk.triggerFires(id),
    queryFn: () => call('listTriggerFires', { params: { id }, query: { limit: 50 } }),
    enabled,
  });
export const useMcpServers = () =>
  useQuery({ queryKey: qk.mcpServers, queryFn: () => call('listMcpServers') });
export const useToolPolicies = () =>
  useQuery({ queryKey: qk.toolPolicies, queryFn: () => call('listToolPolicies') });
export const useAudit = (enabled = true) =>
  useQuery({ queryKey: qk.audit, queryFn: () => call('auditLog'), enabled });
export type AuditFilters = { actorId?: string; action?: string; from?: string; to?: string };
export const useAuditPage = (f: AuditFilters) =>
  useQuery({
    queryKey: [...qk.audit, f],
    queryFn: () => call('auditLog', { query: { ...f, limit: 200 } }),
    placeholderData: (prev) => prev,
  });
export const useAuditFacets = () =>
  useQuery({ queryKey: qk.auditFacets, queryFn: () => call('auditFacets') });
export const useMemberList = () => useQuery({ queryKey: qk.memberList, queryFn: () => call('listMembers') });
export const useNotifyChannels = () =>
  useQuery({ queryKey: qk.notifyChannels, queryFn: () => call('listNotifyChannels') });
export const useChannels = () => useQuery({ queryKey: qk.channels, queryFn: () => call('listChannels') });
export const useChannelPosts = (id: string | null) =>
  useQuery({
    queryKey: qk.channelPosts(id ?? ''),
    enabled: !!id,
    queryFn: () => call('listChannelPosts', { params: { id: id! }, query: { limit: 100 } }),
  });
export const useChannelThread = (id: string | null, thread: string | null) =>
  useQuery({
    queryKey: qk.channelThread(id ?? '', thread ?? ''),
    enabled: !!id && !!thread,
    queryFn: () => call('listChannelPosts', { params: { id: id! }, query: { limit: 100, thread: thread! } }),
  });
export const useChannelSearch = (q: string) =>
  useQuery({
    queryKey: qk.channelSearch(q),
    enabled: q.trim().length >= 2,
    queryFn: () => call('searchChannelPosts', { query: { q: q.trim(), limit: 20 } }),
  });
export const useApiTokens = () => useQuery({ queryKey: qk.apiTokens, queryFn: () => call('listApiTokens') });
export const useNotifyRules = () =>
  useQuery({ queryKey: qk.notifyRules, queryFn: () => call('listNotifyRules') });
export const useDeliveries = () =>
  useQuery({ queryKey: qk.deliveries, queryFn: () => call('listDeliveries', { query: { limit: 50 } }) });
export const useTelegramStatus = (poll = false) =>
  useQuery({
    queryKey: qk.telegram,
    queryFn: () => call('telegramStatus'),
    refetchInterval: poll ? 2500 : false,
  });

// ---------- skills ----------
export const useSkills = () =>
  useQuery({ queryKey: qk.skills, queryFn: () => call('listSkills', { query: { limit: 200 } }) });
export const useSkillSuggestions = () =>
  useQuery({ queryKey: qk.skillSuggestions, queryFn: () => call('listSkillSuggestions') });
export const useSkill = (id: string) =>
  useQuery({ queryKey: qk.skill(id), queryFn: () => call('getSkill', { params: { id } }) });
export const useSkillVersions = (id: string) =>
  useQuery({ queryKey: qk.skillVersions(id), queryFn: () => call('listSkillVersions', { params: { id } }) });
/** Catalog exposures / loads per skill (deterministic aggregate; changes slowly, so cached a while). */
export const useSkillUsage = () =>
  useQuery({ queryKey: qk.skillUsage, queryFn: () => call('listSkillUsage'), staleTime: 300_000 });

// ---------- recording mode ----------
export const useFlows = () =>
  useQuery({ queryKey: qk.flows, queryFn: () => call('listFlows', { query: { limit: 200 } }) });
export const useFlow = (id: string) =>
  useQuery({ queryKey: qk.flow(id), queryFn: () => call('getFlow', { params: { id } }) });
export const useFlowVersions = (id: string) =>
  useQuery({ queryKey: qk.flowVersions(id), queryFn: () => call('listFlowVersions', { params: { id } }) });
export const useFlowSuggestions = (id: string, enabled = true) =>
  useQuery({
    queryKey: qk.flowSuggestions(id),
    queryFn: () => call('suggestFlowAgents', { params: { id } }),
    enabled,
  });
export const useRecordings = () =>
  useQuery({ queryKey: qk.recordings, queryFn: () => call('listRecordings', { query: { limit: 50 } }) });
export const useRecording = (id: string) =>
  useQuery({ queryKey: qk.recording(id), queryFn: () => call('getRecording', { params: { id } }) });
export const useRecordingEvents = (id: string, live: boolean) =>
  useQuery({
    queryKey: qk.recordingEvents(id),
    queryFn: () => call('listRecordingEvents', { params: { id }, query: { limit: 1000 } }),
    // Live org events invalidate this while recording; the poll is a fallback.
    refetchInterval: live ? 5_000 : false,
  });

/** Generic typed mutation over a route; invalidates the given keys on success. */
export function useApiMutation<
  K extends keyof BodyMap | 'deleteProvider' | 'deleteAgent' | 'deleteTrigger' | 'deleteMcpServer',
>(key: K, opts?: { invalidate?: QueryKey[]; success?: string }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      params?: Record<string, string>;
      body?: K extends keyof BodyMap ? BodyMap[K] : undefined;
    }) =>
      // biome-ignore lint/suspicious/noExplicitAny: generic passthrough to the typed client
      call(key as any, vars as any),
    onSuccess: () => {
      for (const k of opts?.invalidate ?? []) qc.invalidateQueries({ queryKey: k });
      if (opts?.success) toast.success(opts.success);
    },
  });
}

// ---------- live org events ----------
const INVALIDATE_BY_KIND: Record<string, QueryKey[]> = {
  resync: [
    ['agents'],
    ['runs'],
    ['run'],
    ['messages'],
    ['conversations'],
    ['master', 'conversations'],
    ['approvals'],
    qk.questions,
    qk.egressRequests,
    qk.tasks,
    qk.notifications,
    qk.orgStatus,
    qk.companyFallback,
    qk.onboarding,
    ['channels'],
  ],
  halted: [qk.orgStatus, ['runs'], qk.agents],
  resumed: [qk.orgStatus],
  agent: [qk.agents, qk.orgStatus, qk.onboarding],
  // A finished turn may be an unread reply (chat list badges).
  run: [['runs'], ['run'], qk.tasks, qk.questions, qk.orgStatus, qk.agents, ['conversations']],
  task: [qk.tasks, ['runs'], qk.agents, qk.orgStatus],
  // A report back from a delegated task landed in a conversation (and may have started a turn).
  message: [['messages'], ['conversations'], ['master', 'conversations'], ['runs']],
  approval: [['approvals'], qk.egressRequests, ['runs'], ['run'], qk.tasks, qk.orgStatus, qk.agents],
  notification: [qk.notifications, qk.orgStatus],
  org: [qk.orgStatus, qk.companyFallback],
  usage: [['usage'], qk.orgStatus, qk.agents],
  trigger: [['triggers'], qk.tasks],
  channel: [['channels']],
  mcp: [qk.mcpServers],
  policy: [qk.toolPolicies],
  flows: [qk.flows, qk.recordings],
  skills: [qk.skills, qk.agents],
  recording: [qk.recordings],
};

/** Subscribe to `/api/events` and invalidate the affected queries. Mount once in the app shell. */
export function useOrgEvents(enabled: boolean): boolean {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    const c = connectSSE({
      url: ROUTES.events.path,
      onStateChange: (s) => setConnected(s === 'open'),
      onMessage: (msg) => {
        let parsed: OrgEvent;
        try {
          const r = OrgEvent.safeParse(JSON.parse(msg.data));
          if (!r.success) return;
          parsed = r.data;
        } catch {
          return;
        }
        const prefix = parsed.kind.split(/[.:]/)[0] ?? parsed.kind;
        // Org notifications have no replay log. Catch up after every connection, including
        // changes between the initial query fetch and subscribing to the stream.
        const keys = INVALIDATE_BY_KIND[prefix === 'connected' ? 'resync' : prefix] ?? [qk.orgStatus];
        // Let a fetch that is already running finish (cancelRefetch: false). A burst of events
        // (or a reconnect resync) would otherwise cancel the first load again and again, and a
        // page that is still `pending` (e.g. Tasks) would spin forever.
        for (const k of keys) qc.invalidateQueries({ queryKey: k }, { cancelRefetch: false });
        orgEvents.emit(parsed);
        if (parsed.kind === 'halted')
          toast.error('Organization HALTED — all agents stopped', { id: 'halted' });
      },
    });
    return () => c.close();
  }, [enabled, qc]);
  return connected;
}
